import {Checkbox, Divider, Modal, Select, Textarea} from '@mantine/core';
import {MonthPickerInput} from '@mantine/dates';
import type {FormErrors} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import classNames from 'classnames';
import {type FC, useCallback, useEffect, useState} from 'react';
import {MdCalendarMonth, MdDelete} from 'react-icons/md';
import type {z} from 'zod';
import 'dayjs/locale/ja';
import {DevelopmentExperienceBodyLength} from '../../core/entities/mypage/constants';
import type {HashTagSchema} from '../../core/entities/register/schema';
import {type MultiSelectItem, MultiSelectWrapper} from './MultiSelectWrapper';

type hashtagType = z.infer<typeof HashTagSchema>;
type itemsType = {value: string; label: string};

export type DevelopmentExperienceType = {
  id?: number;
  categoryID: number | undefined;
  body: string;
  startDate?: string;
  endDate?: string;
  programmingLanguageTags?: hashtagType[];
  libraryFrameworkTags?: hashtagType[];
  otherTags?: hashtagType[];
};

export const DefaultExperience: DevelopmentExperienceType = {
  categoryID: undefined,
  body: '',
  startDate: undefined,
  endDate: undefined,
  programmingLanguageTags: [],
  libraryFrameworkTags: [],
  otherTags: [],
};

type Props = {
  maxSize?: number;
  onExperiencesChange: (experiences: DevelopmentExperienceType[]) => void;
  experiences: DevelopmentExperienceType[];
  categories: itemsType[];
  languages: MultiSelectItem[];
  frameworks: MultiSelectItem[];
  others: MultiSelectItem[];
  errors: FormErrors;
  errorsKey?: string;
  showAlertMessage?: boolean;
};

export const DevelopmentExperiences: FC<Props> = ({
  maxSize = 10,
  onExperiencesChange,
  experiences,
  categories,
  languages,
  frameworks,
  others,
  errors,
  errorsKey = 'experiences',
  showAlertMessage = false,
}) => {
  const addExperience = useCallback(() => {
    const newExperiences = [...experiences, DefaultExperience];
    onExperiencesChange(newExperiences);
  }, [experiences, onExperiencesChange]);

  const deleteExperience = useCallback(
    (index: number) => {
      const newExperiences = experiences.filter((_, i) => i !== index);
      onExperiencesChange(newExperiences);
    },
    [experiences, onExperiencesChange],
  );

  const onUpdate = useCallback(
    (index: number, experience: DevelopmentExperienceType) => {
      const newExperiences = [...experiences];
      newExperiences[index] = {
        ...experience,
      };
      onExperiencesChange(newExperiences);
    },
    [experiences, onExperiencesChange],
  );

  return (
    <div className="flex flex-col">
      {experiences.map((experience, index) => (
        <DevelopmentExperience
          key={index}
          index={index}
          experience={experience}
          categories={categories}
          languages={languages}
          frameworks={frameworks}
          others={others}
          onDelete={deleteExperience}
          onUpdate={onUpdate}
          errors={errors}
          errorsKey={errorsKey}
          showAlertMessage={showAlertMessage}
        />
      ))}
      <Divider variant="dashed" className="my-16" />
      {experiences.length < maxSize && (
        <div className="flex justify-center">
          <div
            className="text-primary-1 py-8 px-48 border border-primary-1 border-1 bg-white shadow-card rounded-full hover:bg-primary-1 hover:text-white cursor-pointer"
            onClick={addExperience}
            id="add-development-experience"
          >
            ＋ 具体事例を追加する
          </div>
        </div>
      )}
    </div>
  );
};

type DevelopmentExperienceProps = {
  index: number;
  experience: DevelopmentExperienceType;
  categories: Array<{value: string; label: string}>;
  languages: MultiSelectItem[];
  frameworks: MultiSelectItem[];
  others: MultiSelectItem[];
  onDelete?: (index: number) => void;
  onUpdate: (index: number, experience: DevelopmentExperienceType) => void;
  errors: FormErrors;
  canDelete?: boolean;
  errorsKey: string;
  showAlertMessage: boolean;
};

export const DevelopmentExperience: FC<DevelopmentExperienceProps> = ({
  index,
  experience,
  categories,
  languages,
  frameworks,
  others,
  onDelete,
  onUpdate,
  errors,
  canDelete = true,
  errorsKey,
  showAlertMessage,
}) => {
  const handleChange = useCallback(
    (
      field: keyof DevelopmentExperienceType,
      value: string | number | hashtagType[] | undefined,
    ) => {
      onUpdate(index, {...experience, [field]: value});
    },
    [index, experience, onUpdate],
  );
  const [opened, {open, close}] = useDisclosure();
  const [checkedEndDate, setCheckedEndDate] = useState(false);
  const findTag = (values: MultiSelectItem[], value: string): hashtagType => {
    const tag = values.find((v) => v.value === value);
    return {
      hashtagID: Number(tag?.value),
      hashtagName: tag?.label ?? '',
    };
  };

  useEffect(() => {
    // 既存データ読み込み時
    // StartDateが設定されていてendDateが設定されていない場合、現在も継続中として扱う
    if (experience.startDate && !experience.endDate) {
      setCheckedEndDate(true);
    }
  }, [experience.id]);

  return (
    <div className="mt-16">
      <Divider variant="dashed" />
      <div>
        {errors[`${errorsKey}.${index}`] && (
          <p className="text-base text-notification mt-8">
            {errors[`${errorsKey}.${index}`]}
          </p>
        )}
      </div>
      <div
        className={classNames('w-full py-24', {
          'bg-background-4': opened,
        })}
      >
        <div className="flex justify-between items-center">
          <Select
            className="w-full max-w-[60%]"
            data={categories}
            placeholder="カテゴリを選択してください"
            value={experience.categoryID?.toString() ?? ''}
            onChange={(value) => {
              handleChange('categoryID', Number(value) || undefined);
            }}
            radius={16}
          />
          {onDelete && (
            <div
              className={`flex items-start text-primary-1 text-sm cursor-pointer hover:opacity-50 ${canDelete ? '' : 'hidden'}`}
              onClick={open}
            >
              <MdDelete size={16} />
              <div>この事例を削除</div>
            </div>
          )}
        </div>
        <div className="mt-24">
          <Textarea
            placeholder="開発経験を入力してください"
            maxLength={DevelopmentExperienceBodyLength}
            minRows={5}
            autosize
            value={experience.body}
            radius={16}
            onChange={(e) => {
              handleChange('body', e.currentTarget.value);
            }}
          />
          <div className="text-right text-sm text-black-2 mt-16">
            {experience.body.length}/{DevelopmentExperienceBodyLength}文字
          </div>
          <div className="mt-8">
            <div className="font-bold text-black-1 text-base mb-8">期間</div>
            <div className="flex justify-between items-center gap-8">
              <MonthPickerInput
                icon={<MdCalendarMonth />}
                className="w-full"
                description="開始年月"
                valueFormat="YYYY/M"
                locale="ja"
                value={
                  experience.startDate ? new Date(experience.startDate) : null
                }
                clearable
                radius={16}
                onChange={(e) => {
                  handleChange('startDate', e?.toString() ?? undefined);
                }}
              />
              <div className="mt-auto pb-8">〜</div>
              <MonthPickerInput
                icon={<MdCalendarMonth />}
                className="w-full"
                description="終了年月"
                valueFormat="YYYY/M"
                disabled={checkedEndDate}
                locale="ja"
                value={experience.endDate ? new Date(experience.endDate) : null}
                clearable
                radius={16}
                onChange={(e) => {
                  handleChange('endDate', e?.toString() ?? undefined);
                }}
              />
            </div>
            <div className="flex justify-start items-center mt-12 px-8">
              <Checkbox
                label="現在も継続中"
                checked={checkedEndDate}
                onChange={(e) => {
                  setCheckedEndDate(e.currentTarget.checked);
                  if (e.currentTarget.checked) {
                    handleChange('endDate', undefined);
                  }
                }}
              />
            </div>
          </div>
          <div className="mt-24">
            <div className="font-bold text-black-1 text-base mb-8">
              使用した開発言語
            </div>
            <MultiSelectWrapper
              data={languages}
              value={
                experience.programmingLanguageTags?.map((value) =>
                  value.hashtagID.toString(),
                ) ?? []
              }
              placeholder="使用した開発言語を選択してください"
              onSelectedItemsChange={(selectedItems: string[]) => {
                const tags = selectedItems.map((value) =>
                  findTag(languages, value),
                );
                handleChange('programmingLanguageTags', tags);
              }}
              searchable={true}
            />
          </div>
          <div className="mt-24">
            <div className="font-bold text-black-1 text-base mb-8">
              使用したライブラリ、フレームワーク
            </div>
            <MultiSelectWrapper
              data={frameworks}
              value={
                experience.libraryFrameworkTags?.map((value) =>
                  value.hashtagID.toString(),
                ) ?? []
              }
              placeholder="使用したライブラリ、フレームワークを選択してください"
              onSelectedItemsChange={(selectedItems: string[]) => {
                const tags = selectedItems.map((value) =>
                  findTag(frameworks, value),
                );
                handleChange('libraryFrameworkTags', tags);
              }}
              searchable={true}
            />
          </div>
          <div className="mt-24">
            <div className="font-bold text-black-1 text-base mb-8">
              その他（インフラ、データベースなど）
            </div>
            <MultiSelectWrapper
              data={others}
              value={
                experience.otherTags?.map((value) =>
                  value.hashtagID.toString(),
                ) ?? []
              }
              onSelectedItemsChange={(selectedItems: string[]) => {
                const tags = selectedItems.map((value) =>
                  findTag(others, value),
                );
                handleChange('otherTags', tags);
              }}
              searchable={true}
            />
          </div>
        </div>
        {onDelete && (
          <DeleteModal
            opened={opened}
            onClose={close}
            onDelete={() => {
              onDelete(index);
            }}
            showAlertMessage={showAlertMessage}
          />
        )}
      </div>
    </div>
  );
};

const DeleteModal: FC<{
  opened: boolean;
  onClose: () => void;
  onDelete: () => void;
  showAlertMessage: boolean;
}> = ({opened, onClose, onDelete, showAlertMessage}) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      radius={16}
    >
      <div className="p-16 text-center">
        <div className="mb-24 text-xl font-bold text-black">
          この具体事例を削除しますか？
        </div>
        {showAlertMessage ? (
          <div className="mb-24 text-notification text-justify">
            この操作は取り消しできません。プロフィールにも削除が反映されます。
          </div>
        ) : (
          <div className="mb-24 text-[16px] text-black">
            この操作は取り消しできません。
          </div>
        )}
        <div className="flex justify-center gap-12">
          <button
            className="w-[188px] px-16 py-[10px] text-center border-2 rounded-full pc:py-8 cursor-pointer border-labels-2 text-black-1  hover:opacity-60"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="w-[188px] px-16 py-[10px] text-center border-2 rounded-full pc:py-8 border-notification-2 bg-notification-2 text-white font-bold cursor-pointer hover:opacity-60"
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            削除する
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DevelopmentExperiences;
